import React, { useRef, useState } from 'react';
import { Table } from '@tanstack/react-table';
import { Check, ChevronDown, ChevronUp, MoreVertical, Trash2, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { BooleanCell, StandardCell } from './TableCell';

interface TableBodyProps {
  table: Table<any>;
  columns: any[];
  rows: any[];
  cells: any[];
  isMobile: boolean;
  needsDesktopScrolling?: boolean; // Flag to indicate if desktop is in scrolling mode
  editingCell: { rowId: string; columnId: string } | null;
  setEditingCell: React.Dispatch<React.SetStateAction<{ rowId: string; columnId: string } | null>>;
  cellMenuOpen: { rowId: string; columnId: string } | null;
  setCellMenuOpen: React.Dispatch<React.SetStateAction<{ rowId: string; columnId: string } | null>>;
  formatValueForDisplay: (value: any, type: string) => string;
  tableDefinition: any;
  deleteRow: (tableId: string, rowId: string) => Promise<void>;
  updateCell: (rowId: string, columnId: string, value: string) => Promise<void>;
  addRow: (tableId: string, data: any) => Promise<any>;
  updateRow: (tableId: string, rowId: string, updates: any) => Promise<any>;
  handleAddRow?: () => void;
}

// Interface for row action button state
interface RowActionState {
  rowId: string;
  isMenuOpen: boolean;
  isConfirmingDelete: boolean;
  isMovingRow: boolean;
}

// Dropdown menu for row actions
interface RowActionsMenuProps {
  rowId: string;
  tableId: string;
  rowIndex: number;
  rowsCount: number;
  onClose: () => void;
  onDeleteConfirm: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  rowActionState: RowActionState | null;
  setRowActionState: React.Dispatch<React.SetStateAction<RowActionState | null>>;
}

function RowActionsMenu({
  rowIndex,
  rowsCount,
  onClose,
  onDeleteConfirm,
  onMoveUp,
  onMoveDown,
  rowActionState,
  setRowActionState,
}: RowActionsMenuProps) {
  // Determine if the move up/down actions should be disabled
  const isFirstRow = rowIndex === 0;
  const isLastRow = rowIndex === rowsCount - 1;

  return (
    <div className="row-action-menu bg-white rounded-md z-50 border border-gray-200 py-1 overflow-hidden min-w-[200px] w-[200px]">
      {/* Move Up Option */}
      <button
        className={`w-full text-left px-4 py-3 text-sm flex items-center ${
          isFirstRow
            ? 'opacity-50 cursor-not-allowed text-gray-400'
            : 'hover:bg-gray-100 text-gray-700 transition-colors'
        }`}
        onClick={() => {
          if (!isFirstRow) {
            onMoveUp();
            onClose();
          }
        }}
        disabled={isFirstRow}
        aria-disabled={isFirstRow}
        title={isFirstRow ? 'Already at the top' : 'Move row up'}
      >
        <ChevronUp className="mr-3 h-4 w-4 flex-shrink-0" />
        <span>Move Up</span>
      </button>

      {/* Move Down Option */}
      <button
        className={`w-full text-left px-4 py-3 text-sm flex items-center ${
          isLastRow
            ? 'opacity-50 cursor-not-allowed text-gray-400'
            : 'hover:bg-gray-100 text-gray-700 transition-colors'
        }`}
        onClick={() => {
          if (!isLastRow) {
            onMoveDown();
            onClose();
          }
        }}
        disabled={isLastRow}
        aria-disabled={isLastRow}
        title={isLastRow ? 'Already at the bottom' : 'Move row down'}
      >
        <ChevronDown className="mr-3 h-4 w-4 flex-shrink-0" />
        <span>Move Down</span>
      </button>

      {/* Delete Option - with a border at the top to separate it */}
      <div className="border-t border-gray-200 mt-1"></div>
      {rowActionState?.isConfirmingDelete ? (
        <div className="px-4 py-3 space-y-2">
          <div className="text-sm text-gray-700 mb-3">Delete this row?</div>
          <div className="flex items-center space-x-3">
            <button
              className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-medium py-1 px-2 rounded flex items-center justify-center"
              onClick={() => {
                if (onDeleteConfirm) onDeleteConfirm();
              }}
            >
              <Check size={12} className="mr-1" /> Confirm
            </button>
            <button
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium py-1 px-2 rounded flex items-center justify-center"
              onClick={() => {
                setRowActionState(prev => ({
                  ...prev!,
                  isConfirmingDelete: false,
                }));
              }}
            >
              <X size={12} className="mr-1" /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          className="w-full text-left px-4 py-3 text-sm flex items-center text-red-600 hover:bg-red-50 transition-colors"
          onClick={() => {
            setRowActionState(prev => ({
              ...prev!,
              isConfirmingDelete: true,
            }));
          }}
          title="Delete row"
        >
          <Trash2 className="mr-3 h-4 w-4 flex-shrink-0" />
          <span>Delete</span>
        </button>
      )}
    </div>
  );
}

// Note: Delete confirmation now handled within the RowActionsMenu

export function TableBody({
  table,
  columns,
  rows,
  cells,
  isMobile,
  needsDesktopScrolling = false,
  editingCell,
  setEditingCell,
  cellMenuOpen,
  setCellMenuOpen,
  formatValueForDisplay,
  tableDefinition,
  deleteRow,
  updateCell,
  addRow,
  updateRow,
}: TableBodyProps) {
  // State for row action menus and delete confirmations
  const [rowActionState, setRowActionState] = useState<RowActionState | null>(null);

  // Helper to close any open action menu
  const closeRowActionMenu = () => {
    // Don't close if we're in the middle of moving a row
    if (rowActionState?.isMovingRow) return;
    setRowActionState(null);
  };

  // Add click outside handler for the menu
  React.useEffect(() => {
    // Only add listener if a menu is open
    if (!rowActionState?.isMenuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Skip if we're in delete confirmation mode or moving a row
      if (rowActionState.isConfirmingDelete || rowActionState.isMovingRow) return;

      const target = e.target as HTMLElement;

      // Check if the click was inside a row action button or menu
      const isMenuClick =
        !!target.closest('.row-action-menu') || !!target.closest('.row-action-button');

      // If click was outside, close the menu
      if (!isMenuClick) {
        closeRowActionMenu();
      }
    };

    // Add the event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [rowActionState]);

  // Handler for moving rows up and down
  const handleMoveRow = async (rowId: string, direction: 'up' | 'down') => {
    if (!tableDefinition?.id) return;

    // Set loading state
    setRowActionState(() => ({
      rowId,
      isMenuOpen: false,
      isConfirmingDelete: false,
      isMovingRow: true,
    }));

    // Sort rows by order_index to ensure we're working with properly ordered rows
    const sortedRows = [...rows].sort((a, b) => a.order_index - b.order_index);

    // Find current row index in the sorted array
    const rowIndex = sortedRows.findIndex(r => r.id === rowId);
    if (rowIndex === -1) {
      // Reset state if row not found
      setRowActionState(null);
      return;
    }

    // Check for first/last row - though the buttons should be disabled in the UI,
    // we'll add another check here for safety
    if (direction === 'up' && rowIndex === 0) {
      console.log('Already at the top row, cannot move up');
      setRowActionState(null);
      return;
    }

    if (direction === 'down' && rowIndex === sortedRows.length - 1) {
      console.log('Already at the bottom row, cannot move down');
      setRowActionState(null);
      return;
    }

    // Calculate the target index
    const targetIndex = direction === 'up' ? rowIndex - 1 : rowIndex + 1;

    // Get the current and target rows
    const currentRow = sortedRows[rowIndex];
    const targetRow = sortedRows[targetIndex];

    // Get their current order_index values
    const currentOrderIndex = currentRow.order_index;
    const targetOrderIndex = targetRow.order_index;

    try {
      console.log(`Moving row ${rowId} ${direction} by swapping with ${targetRow.id}`);
      console.log(`Swapping order_index ${currentOrderIndex} with ${targetOrderIndex}`);

      // Update the rows in the database (simple swap)
      await Promise.all([
        updateRow(tableDefinition.id, currentRow.id, { order_index: targetOrderIndex }),
        updateRow(tableDefinition.id, targetRow.id, { order_index: currentOrderIndex }),
      ]);

      // Force a table refresh by calling table.reset()
      table.reset();

      // Reset loading state after successful update with a small delay
      // to ensure the store has time to update and the animation is visible
      setTimeout(() => {
        setRowActionState(null);
      }, 300);
    } catch (error) {
      console.error('Failed to update row order:', error);
      // Reset loading state on error with a small delay
      setTimeout(() => {
        setRowActionState(null);
      }, 300);
    }
  };
  // Create refs for rows
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});

  return (
    <tbody className="h-full">
      {/* Map over actual table rows */}
      {table.getRowModel().rows.map(row => (
        <tr
          key={row.id}
          className="border-b border-gray-200 hover-row group"
          data-row-id={row.original.id}
          ref={el => (rowRefs.current[row.id] = el)}
        >
          {/* Line number column with action menu */}
          <td className="w-10 min-w-10 p-0 relative sticky left-0 z-10 border-r border-gray-200">
            <div className="h-full flex items-center justify-center relative">
              {/* Row number display */}
              <span
                className={`text-xs text-gray-500 ${rowActionState?.rowId === row.original.id ? 'opacity-0' : 'group-hover:opacity-0'} transition-opacity`}
              >
                {(() => {
                  // Get all rows sorted by order_index
                  const sortedRows = [...rows].sort((a, b) => a.order_index - b.order_index);
                  // Find the index of this row in the sorted array
                  const sortedIndex = sortedRows.findIndex(r => r.id === row.original.id);
                  // Return the 1-based index
                  return sortedIndex !== -1 ? sortedIndex + 1 : '';
                })()}
              </span>

              {/* Show loading spinner or ellipsis button based on state */}
              {rowActionState?.rowId === row.original.id && rowActionState.isMovingRow ? (
                <div className="absolute inset-0 flex items-center justify-center opacity-100">
                  <svg
                    className="animate-spin h-4 w-4 text-blue-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              ) : (
                <button
                  onClick={() => {
                    if (rowActionState?.isMovingRow) return; // Prevent interaction during move
                    setRowActionState({
                      rowId: row.original.id,
                      isMenuOpen:
                        !rowActionState?.isMenuOpen || rowActionState?.rowId !== row.original.id,
                      isConfirmingDelete: false,
                      isMovingRow: false,
                    });
                  }}
                  className={`row-action-button absolute inset-0 ${
                    rowActionState?.rowId === row.original.id && rowActionState.isMenuOpen
                      ? 'opacity-100 text-blue-500'
                      : 'opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600'
                  } transition-all flex items-center justify-center`}
                  title="Row actions"
                >
                  <MoreVertical size={16} />
                </button>
              )}

              {/* Row action menu - rendered in portal for proper z-index */}
              {rowActionState?.rowId === row.original.id &&
                rowActionState.isMenuOpen &&
                rowRefs.current[row.id] &&
                createPortal(
                  <div
                    className="fixed z-[9999] shadow-xl"
                    style={{
                      // Calculate position to ensure menu is fully visible within the viewport
                      top: (() => {
                        if (!rowRefs.current[row.id]) return '0px';

                        const rect = rowRefs.current[row.id].getBoundingClientRect();
                        const topPosition = isMobile ? rect.bottom : rect.top;
                        const menuHeight = 150; // Approximate menu height

                        // Ensure menu doesn't go below viewport
                        if (topPosition + menuHeight > window.innerHeight) {
                          return `${window.innerHeight - menuHeight - 10}px`;
                        }

                        return `${topPosition}px`;
                      })(),
                      left: (() => {
                        if (!rowRefs.current[row.id]) return '0px';

                        const rect = rowRefs.current[row.id].getBoundingClientRect();
                        const leftPosition = isMobile ? rect.left : rect.left + 40;
                        const menuWidth = 200; // Approximate menu width

                        // Ensure menu doesn't go beyond right edge of viewport
                        if (leftPosition + menuWidth > window.innerWidth) {
                          return `${window.innerWidth - menuWidth - 10}px`;
                        }

                        return `${leftPosition}px`;
                      })(),
                    }}
                  >
                    <RowActionsMenu
                      rowId={row.original.id}
                      tableId={tableDefinition.id}
                      rowIndex={[...rows]
                        .sort((a, b) => a.order_index - b.order_index)
                        .findIndex(r => r.id === row.original.id)}
                      rowsCount={rows.length}
                      onClose={closeRowActionMenu}
                      onDeleteConfirm={() => {
                        deleteRow(tableDefinition.id, row.original.id);
                        setRowActionState(null);
                      }}
                      onMoveUp={() => handleMoveRow(row.original.id, 'up')}
                      onMoveDown={() => handleMoveRow(row.original.id, 'down')}
                      rowActionState={rowActionState}
                      setRowActionState={setRowActionState}
                    />
                  </div>,
                  document.body
                )}
            </div>
          </td>
          {row.getVisibleCells().map((cell, cellIndex) => {
            const columnDef = columns.find(c => c.id === cell.column.id);
            const value = cell.getValue();
            const formattedValue = formatValueForDisplay(value, columnDef?.type || 'text');
            const rowId = row.original.id;
            const columnId = cell.column.id;
            const isEditing = editingCell?.rowId === rowId && editingCell?.columnId === columnId;
            const isBooleanColumn = columnDef?.type === 'boolean';

            // Make the first content column (cellIndex === 0) sticky in desktop scrolling mode only
            const isFirstContentColumn = cellIndex === 0;
            const shouldBeSticky = needsDesktopScrolling && isFirstContentColumn && !isMobile;

            return (
              <td
                key={cell.id}
                className="h-12 min-h-[48px] p-0 relative border-r border-gray-200"
                style={
                  shouldBeSticky
                    ? {
                        position: 'sticky',
                        left: '40px',
                        zIndex: 5,
                        backgroundColor: 'white',
                        borderRight: '1px solid #e5e7eb',
                      }
                    : undefined
                }
              >
                {isBooleanColumn ? (
                  <BooleanCell
                    value={value}
                    rowId={rowId}
                    columnId={columnId}
                    updateCell={updateCell}
                    isMobile={isMobile}
                    setCellMenuOpen={setCellMenuOpen}
                    cellMenuOpen={cellMenuOpen}
                  />
                ) : (
                  <StandardCell
                    value={value}
                    columnType={columnDef?.type || 'text'}
                    rowId={rowId}
                    columnId={columnId}
                    isEditing={isEditing}
                    isMobile={isMobile}
                    formattedValue={formattedValue}
                    columnOptions={columnDef?.options || {}}
                    updateCell={updateCell}
                    setEditingCell={setEditingCell}
                    setCellMenuOpen={setCellMenuOpen}
                    cellMenuOpen={cellMenuOpen}
                    rows={rows}
                    addRow={addRow}
                    tableDefinition={tableDefinition}
                  />
                )}
              </td>
            );
          })}
        </tr>
      ))}

      {/* We don't need to add empty rows for padding anymore */}

      {/* Totals row for number columns - only show if there are number columns */}
      {columns.some(col => col.type === 'number') && (
        <tr className="bg-gray-100 border-t-2 border-gray-300 border-b border-gray-200">
          {/* Line number cell with "Total" label */}
          <td className="w-10 min-w-10 p-0 relative sticky left-0 z-10 border-r border-gray-200">
            <div className="h-full flex items-center justify-center">
              <span className="text-xs text-gray-700 font-bold">Σ</span>
            </div>
          </td>
          {columns.map((column, colIndex) => {
            const isNumberColumn = column.type === 'number';
            const isFirstColumn = colIndex === 0;
            const shouldBeSticky = needsDesktopScrolling && isFirstColumn && !isMobile;

            // Calculate sum for number columns
            let columnSum = null;
            if (isNumberColumn) {
              columnSum = cells
                .filter(cell => cell.columnId === column.id)
                .reduce((sum, cell) => {
                  const value = parseFloat(cell.value);
                  return isNaN(value) ? sum : sum + value;
                }, 0);
            }

            return (
              <td
                key={column.id}
                className={`h-12 p-3 bg-gray-100 border-r border-gray-200 ${isNumberColumn ? 'text-center font-bold text-blue-700' : ''}`}
                style={
                  shouldBeSticky
                    ? {
                        position: 'sticky',
                        left: '40px',
                        zIndex: 5,
                        borderRight: '1px solid #e5e7eb',
                      }
                    : undefined
                }
              >
                {isNumberColumn ? (
                  <span>
                    {columnSum !== null
                      ? Number.isInteger(columnSum)
                        ? columnSum
                            .toLocaleString(undefined, { useGrouping: true })
                            .replace(/,/g, ' ') // No decimal places for integers
                        : columnSum
                            .toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            .replace(/,/g, ' ')
                      : ''}
                  </span>
                ) : column.id === columns[0].id ? (
                  <span className="text-gray-700 font-bold">Total</span>
                ) : null}
              </td>
            );
          })}
        </tr>
      )}
    </tbody>
  );
}
